import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import {
  UnitSystem,
  useAddWearData,
  useDeleteWearDataMutation,
  useGetInspectionPointQuery,
  useWearDataQuery
} from 'providers/api';
import { usePage } from 'providers/page';
import React from 'react';
import { Trans } from '@lingui/react';
import { useParams } from 'react-router-dom';
import WearDataForm, { WearDataEntryPanelPageParams } from '../WearDataForm';
import { WearDataInputs } from '../WearDataForm/WearDataSchema';
import PipeCrossSection, { RadarValues } from './PipeCrossSection';
import PCSpool from '../MineModel/Spools/PCSpool';

interface WearDataEntryPanelPageProps {
  unitSystemPreference: UnitSystem;
  successPath: string;
  spools: PCSpool[];
  inspectionPointId?: string;
  projectId?: string;
}

const WearDataEntryPanelPage = ({
  unitSystemPreference,
  successPath,
  spools,
  inspectionPointId: propInspectionPointId,
  projectId: propProjectId,
}: WearDataEntryPanelPageProps) => {
  const { entityId: urlProjectId, inspectionPointId: urlInspectionPointId, wearDataId } = useParams<WearDataEntryPanelPageParams>();
  const projectId = propProjectId || urlProjectId as string;
  const inspectionPointId = propInspectionPointId || urlInspectionPointId as string;

  const { data: wearData } = useWearDataQuery(
    { projectId, wearDataId: wearDataId ?? '' },
    { enabled: !!wearDataId },
  );

  const { data: inspectionPointData } = useGetInspectionPointQuery(
    { projectId, inspectionPointId },
  );
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();

  // The point measures a spool, and the spool owns the pipe whose cross-section is drawn.
  const spoolData = spools.find((spool) => spool.spoolId === inspectionPointData?.spoolId);
  const pipeData = spoolData?.pipe;

  const [currentWearData, setCurrentWearData] = React.useState<RadarValues | undefined>(wearData);

  const handleWearDataChange = (updatedData: WearDataInputs) => {
    const numericData: RadarValues = {
      position1: Number(updatedData.position1),
      position2: Number(updatedData.position2),
      position3: Number(updatedData.position3),
      position4: Number(updatedData.position4),
      position5: Number(updatedData.position5),
      position6: Number(updatedData.position6),
      position7: Number(updatedData.position7),
      position8: Number(updatedData.position8),
    };
    setCurrentWearData(numericData);
  };

  const addInspectionPoint = useAddWearData();
  const deleteWearDataMutation = useDeleteWearDataMutation();
  const lastCreatedWearDataIdRef = React.useRef<string | null>(null);
  const publishUndoAddWearData = React.useCallback((createdWearDataId: string) => {
    const handleUndoAddWearData = () => {
      setUndoActionOverride((current) => (current ? {
        ...current,
        enabled: false,
        loading: true,
      } : current));

      deleteWearDataMutation.mutate(
        { projectId: projectId as string, entityId: createdWearDataId },
        {
          onSuccess: () => {
            clearUndoActionOverride();
          },
          onError: () => {
            setUndoActionOverride({
              enabled: true,
              loading: false,
              onClick: handleUndoAddWearData,
            });
          },
        },
      );
    };

    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: handleUndoAddWearData,
    });
  }, [clearUndoActionOverride, deleteWearDataMutation, projectId, setUndoActionOverride]);

  React.useEffect(() => {
    const createdWearDataId = addInspectionPoint.data;
    if (!addInspectionPoint.isSuccess || !createdWearDataId) {
      return;
    }

    if (lastCreatedWearDataIdRef.current === createdWearDataId) {
      return;
    }

    lastCreatedWearDataIdRef.current = createdWearDataId;
    publishUndoAddWearData(createdWearDataId);
  }, [addInspectionPoint.data, addInspectionPoint.isSuccess, publishUndoAddWearData]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflowY: 'auto',
        height: '100%',
        maxHeight: '100vh',
        minHeight: 0,
        padding: 2,
        boxSizing: 'border-box',
      }}
    >
      {currentWearData && pipeData ? (
        <Box
          sx={{
            width: '100%',
            maxWidth: '600px',
            minHeight: 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 2,
            overflow: 'visible',
            padding: 1,
          }}
        >
          <PipeCrossSection
            wearDataNew={currentWearData}
            pipeData={pipeData}
          />
        </Box>
      ) : (
        <Typography variant="subtitle2"><Trans id="Pipe data or wear data is unavailable.">Pipe data or wear data is unavailable.</Trans></Typography>
      )}
      <WearDataForm
        inspectionPointId={inspectionPointId as string}
        unitSystemPreference={unitSystemPreference}
        successPath={successPath.replace(':inspectionPointId', inspectionPointId as string)}
        mutation={addInspectionPoint}
        projectId={projectId as string}
        onChange={handleWearDataChange}
      />
    </Box>
  );
};

export default WearDataEntryPanelPage;
